/**
 * useUserProfile
 *
 * Hook for reading and updating the per-org user display name
 * in the `org_user_profiles` table. Uses RLS-scoped queries
 * (no edge function needed — RLS policies allow own-row access).
 *
 * @module hooks/useUserProfile
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

const MAX_DISPLAY_NAME_LENGTH = 100

const profileKeys = {
  all: ['user-profile'] as const,
  mine: (orgId: string | null) => [...profileKeys.all, orgId ?? 'no-org'] as const,
}

export function useUpdateDisplayName() {
  const { orgId, refreshAuth } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (displayName: string) => {
      const trimmed = displayName.trim()

      // Input validation
      if (!trimmed) {
        throw new Error('Display name cannot be empty')
      }
      if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
        throw new Error(`Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or fewer`)
      }
      if (!orgId) {
        throw new Error('No active organization')
      }

      // Get current user with proper error handling
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw new Error('Authentication error. Please sign in again.')
      if (!user) throw new Error('Not authenticated')

      // Upsert: insert if no row exists, update if it does
      // Table not in generated types — cast required
      // TODO: regenerate supabase types to remove this cast
      const { error } = await (supabase as any)
        .from('org_user_profiles')
        .upsert(
          {
            user_id: user.id,
            organization_id: orgId,
            display_name: trimmed,
          },
          { onConflict: 'user_id,organization_id' }
        )

      if (error) throw new Error(error.message)
    },
    onSuccess: async () => {
      toast.success('Display name updated')
      queryClient.invalidateQueries({ queryKey: profileKeys.mine(orgId) })
      // Refresh auth context so displayName updates everywhere
      await refreshAuth()
    },
    onError: (error: Error) => {
      // Show user-friendly messages for known validation errors,
      // generic message for DB/network errors to avoid leaking internals
      const safeMessages = [
        'Display name cannot be empty',
        `Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or fewer`,
        'No active organization',
        'Authentication error. Please sign in again.',
        'Not authenticated',
      ]
      const message = safeMessages.includes(error.message)
        ? error.message
        : 'Failed to update display name. Please try again.'
      toast.error(message)
    },
  })
}
